/**
 * Product vocabulary for staff surfaces (GDN-3226, PRD §3 and AC-3).
 *
 * The words staff read are Missing, Requested, Received, Waived and Remind.
 * Only four of those are storage states. "Requested" is DERIVED from delivery
 * state and is never a completion status — a delivered message says nothing
 * about whether the patient acted, so it must never be mistaken for Received.
 */

import type {
  DeliveryHistoryResponse,
  DeliveryTouchpoint,
  RequirementRow,
} from './types';

/** What a staff-facing row reads as. */
export type DisplayState = 'missing' | 'requested' | 'received' | 'waived' | 'cancelled';

/**
 * Map a row to its staff-facing state.
 *
 * `display_state` from the backend carries the settled states; "Requested" is
 * layered on top of Missing when `has_active_session` is true.
 *
 * What that flag MEANS is the backend's to define and has already moved twice
 * — first "a session exists", then "an attempt was accepted" (GDN-3356), and
 * GDN-3363 tightens it again to "delivered and not terminally failed". This
 * function deliberately does not restate the current definition: a comment that
 * pins a moving definition is wrong after the next tightening, and the caller
 * passes the flag through unchanged precisely so the display follows the
 * backend without a frontend change.
 */
export const resolveDisplayState = (
  // `display_state` is optional because a caller can legitimately not have one:
  // the patients-list filter block (GDN-3423) carries the storage `state` only.
  // Optional says that; passing an empty string would claim the backend sent a
  // blank presentation string, which is a different and untrue thing.
  row: Pick<RequirementRow, 'state'> & Partial<Pick<RequirementRow, 'display_state'>>,
  hasActiveSession: boolean,
): DisplayState => {
  // Prefer the storage state: display_state is a presentation string, and a
  // backend that adds a value we do not know about should still land somewhere
  // sensible rather than silently reading as Missing.
  switch (row.state) {
    case 'satisfied':
      return 'received';
    case 'waived':
      return 'waived';
    case 'cancelled':
      return 'cancelled';
    case 'open':
      return hasActiveSession ? 'requested' : 'missing';
    default:
      return row.display_state?.toLowerCase() === 'received' ? 'received' : 'missing';
  }
};

export const DISPLAY_STATE_LABELS: Record<DisplayState, string> = {
  missing: 'Missing',
  requested: 'Requested',
  received: 'Received',
  waived: 'Waived',
  cancelled: 'Cancelled',
};

/** A row still owes the practice something. Drives the missing-item count (AC-3). */
export const isOutstanding = (state: DisplayState): boolean =>
  state === 'missing' || state === 'requested';

/**
 * AC-4: the action reads "Send request" until a request exists, then "Remind".
 * Keyed off the same backend-owned flag as the Requested state, so the label
 * and the chip can never disagree about whether this row has been chased.
 */
export const sendActionLabel = (hasActiveSession: boolean): 'Send request' | 'Remind' =>
  hasActiveSession ? 'Remind' : 'Send request';

/**
 * When a touchpoint reached the patient, for ordering and for the "requested by
 * SMS 4 days ago" line. Touchpoints carry four timestamps and any of them can be
 * null; prefer the one that means "the patient could have seen it".
 *
 * Deliberately NOT the attempts array: an attempt records an outbox try
 * (channel, sequence, status) and carries no send time, only `scheduled_at`.
 */
export const touchpointTimestamp = (
  touchpoint: DeliveryTouchpoint,
): string | undefined =>
  touchpoint.delivered_at ?? touchpoint.sent_at ?? touchpoint.queued_at ?? undefined;

/**
 * Most recent touchpoint, newest first — the derived half of AC-3. Returns
 * undefined when nothing has gone out, which is what makes a row read Missing
 * rather than Requested.
 */
export const latestTouchpoint = (
  history: DeliveryHistoryResponse | undefined,
): DeliveryTouchpoint | undefined => {
  const touchpoints = history?.touchpoints ?? [];
  if (touchpoints.length === 0) return undefined;
  return [...touchpoints].sort((a, b) =>
    (touchpointTimestamp(b) ?? '').localeCompare(touchpointTimestamp(a) ?? ''),
  )[0];
};

/**
 * The date on a Requested line, e.g. "Aug 30".
 *
 * One helper for both staff surfaces. They previously formatted the SAME field
 * two different ways — the record section relative ("4 days ago"), the missing
 * list absolute ("8/30/2026") — so one requirement read differently depending
 * on which screen you opened. Absolute short form matches the wording the PM
 * ruled for the bounced-request line (GDN-3363), so the two lines will read
 * alike when that lands.
 *
 * The year appears only when it is not the current one: "Aug 30" is unambiguous
 * for the recent requests a chase list is made of, and misleading for an old one.
 */
export const formatRequestDate = (iso: string | null | undefined): string | undefined => {
  if (!iso) return undefined;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const sameYear = parsed.getFullYear() === new Date().getFullYear();
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

/**
 * The "Requested by SMS Aug 30" line, shared by both staff surfaces so they
 * cannot drift apart again. Channel and date are each optional: a row can be
 * Requested with neither, and the line degrades to plain "Requested".
 */
export const requestedDetail = (
  channel: string | null | undefined,
  iso: string | null | undefined,
): string => {
  const when = formatRequestDate(iso);
  return ['Requested', channel ? `by ${channel.toUpperCase()}` : undefined, when]
    .filter(Boolean)
    .join(' ');
};
/** What staff read for a delivery touchpoint's status (GDN-3398). */
export type DeliveryTone = 'pending' | 'good' | 'bad';

/**
 * Product words for a touchpoint status, and the tone that carries them.
 *
 * The backend's vocabulary is the vendor's: queued, sent, delivered, failed,
 * bounced, dropped, opened, clicked. Staff should not have to know which of
 * those mean the message arrived.
 *
 * An unrecognised status keeps its raw value rather than falling into a
 * reassuring default. A new vendor state rendering as "Sent" would be a quiet
 * lie; rendering as itself is merely ugly, and ugly is visible.
 */
const DELIVERY_STATUS: Record<string, { label: string; tone: DeliveryTone }> = {
  queued: { label: 'Queued', tone: 'pending' },
  sent: { label: 'Sent', tone: 'pending' },
  delivered: { label: 'Delivered', tone: 'good' },
  opened: { label: 'Opened', tone: 'good' },
  clicked: { label: 'Link opened', tone: 'good' },
  failed: { label: 'Not delivered', tone: 'bad' },
  dropped: { label: 'Not delivered', tone: 'bad' },
  bounced: { label: 'Bounced', tone: 'bad' },
};

export const deliveryStatusLabel = (status: string): string =>
  DELIVERY_STATUS[status?.toLowerCase()]?.label ?? status;

export const deliveryStatusTone = (status: string): DeliveryTone =>
  DELIVERY_STATUS[status?.toLowerCase()]?.tone ?? 'pending';
